import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePlan } from "@/context/PlanContext";
import { UpgradeCard } from "@/components/ProGate";
import { Button } from "@/components/ui/button";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2, Pencil, ExternalLink, List, LayoutGrid,
  Loader2, Star, RefreshCw, Inbox, Megaphone, Settings2, Bell, Clock, HelpCircle, Lock, Flame, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import CatalystForm from "@/components/CatalystForm";
import CatalystDetail from "@/components/CatalystDetail";
import SubmitEventDialog from "@/components/SubmitEventDialog";
import ReviewQueue from "@/components/ReviewQueue";
import SourcesPanel from "@/components/SourcesPanel";
import StarButton from "@/components/StarButton";
import { EventBadges } from "@/components/CatalystBadges";
import { useStars } from "@/lib/useStars";
import UpNextList from "@/components/UpNextList";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { catMeta, dayKey, CATEGORY_ORDER, CATEGORY_META, fmtAgo, isTbd, isCancelled, isUnpublished } from "@/lib/catalysts";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const fmtDay = (d) => new Date(d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

export default function CatalystCalendar() {
  const { user, openAuth } = useAuth();
  const { plan, pro } = usePlan();
  const locked = !!plan && !pro;
  const isAdmin = user && user.role === "admin";
  const [allEvents, setEvents] = useState([]);
  const [pubFilter, setPubFilter] = useState("all");
  const events = useMemo(() => {
    if (!isAdmin || pubFilter === "all") return allEvents;
    return allEvents.filter((e) => (pubFilter === "unpublished" ? isUnpublished(e) : !isUnpublished(e)));
  }, [allEvents, pubFilter, isAdmin]);
  const unpublishedCount = useMemo(() => allEvents.filter(isUnpublished).length, [allEvents]);
  const publishedCount = useMemo(() => allEvents.filter((e) => !e.private && !isUnpublished(e)).length, [allEvents]);
  const unlockEvent = async (e) => {
    try {
      await api.delete(`/catalysts/${e.id}/lock`);
      setEvents((prev) => prev.map((x) => (x.id === e.id ? { ...x, adminLocked: [] } : x)));
      setDetail((d) => (d && d.id === e.id ? { ...d, adminLocked: [] } : d));
      toast.success("Feeds can update this event again");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const togglePublish = async (e) => {
    const published = isUnpublished(e);
    try {
      await api.patch(`/catalysts/${e.id}/publish`, { published });
      setEvents((prev) => prev.map((x) => (x.id === e.id ? { ...x, review: published ? "approved" : "hidden" } : x)));
      setDetail((d) => (d && d.id === e.id ? { ...d, review: published ? "approved" : "hidden" } : d));
      toast.success(published ? "Published — Pro members can see it now" : "Hidden from Pro members");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const isMobile = useIsMobile();
  const [view, setView] = useState(() => (window.matchMedia("(max-width: 767px)").matches ? "upnext" : "calendar"));
  const [dayOpen, setDayOpen] = useState(null);
  useEffect(() => {
    if (isMobile && view === "agenda") setView("upnext");
    if (!isMobile && view === "upnext") setView("calendar");
  }, [isMobile, view]);
  const [formOpen, setFormOpen] = useState(false);
  const [formPrivate, setFormPrivate] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [defaultDate, setDefaultDate] = useState("");
  const [detail, setDetail] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncInfo, setSyncInfo] = useState(null);
  const { isStarred, toggle: toggleStar, starredIds, alerts, viralIds, markAlertsSeen, reload: reloadStars } = useStars();

  const load = useCallback(async () => {
    try {
      const [{ data }, st, nw] = await Promise.all([
        api.get("/catalysts"),
        api.get("/catalysts/sync/status").catch(() => ({ data: null })),
        isAdmin ? api.get("/catalysts/health").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setEvents(data);
      const lastOfficial = st?.data?.at; const lastNews = nw?.data?.newsScan?.at;
      setSyncInfo({ at: [lastOfficial, lastNews].filter(Boolean).sort().pop() || null, official: lastOfficial, news: lastNews });
      if (nw?.data) setPendingCount(nw.data.pendingCount || 0);
    } catch { /* public endpoint; ignore */ } finally { setLoading(false); }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);
  const refreshAll = () => { load(); reloadStars(); };

  const byDay = useMemo(() => {
    const m = {};
    for (const e of events) { if (isTbd(e)) continue; const k = dayKey(e.date); (m[k] ||= []).push(e); }
    if (isAdmin) for (const k in m) m[k].sort((a, b) => Number(isUnpublished(a)) - Number(isUnpublished(b)));
    return m;
  }, [events, isAdmin]);

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first); start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const upcoming = useMemo(() => {
    const today = dayKey(new Date());
    return events.filter((e) => !isTbd(e) && dayKey(e.date) >= today).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [events]);
  const tbd = useMemo(() => events.filter((e) => isTbd(e) && !isCancelled(e)).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")), [events]);
  const feed = useMemo(() => [...upcoming, ...tbd].filter((e) => starredIds.has(e.id) || viralIds.has(e.id) || e.viral), [upcoming, tbd, starredIds, viralIds]);
  const listed = view === "feed" ? feed : upcoming;
  const todayKey = dayKey(new Date());

  useEffect(() => { if (view === "feed" && alerts.length) { const t = setTimeout(markAlertsSeen, 4000); return () => clearTimeout(t); } }, [view, alerts.length, markAlertsSeen]);

  const openAdd = (dateStr, isPrivate = false) => { setEditEvent(null); setFormPrivate(isPrivate); setDefaultDate(dateStr || ""); setFormOpen(true); };
  const openMine = (dateStr) => {
    if (!user) { toast("Log in to add your own events"); openAuth(); return; }
    if (!pro) { toast.error("Private events are a Pro feature", { action: { label: "Go Pro", onClick: () => (window.location.href = "/pricing") } }); return; }
    openAdd(dateStr, true);
  };
  const openEdit = (e) => { setDetail(null); setReviewOpen(false); setEditEvent(e); setFormPrivate(Boolean(e.private)); setDefaultDate(""); setFormOpen(true); };
  const del = async (e) => {
    try { await api.delete(e.private ? `/catalysts/mine/${e.id}` : `/catalysts/${e.id}`); toast.success("Deleted"); setDetail(null); refreshAll(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const onSuggestion = (sug) => toast("Heads up — this looks like it's already on the public calendar", {
    description: `"${sug.title}"${sug.date ? ` · ${sug.date}` : ""}. Your private copy was saved; you can star the public one too.`,
    duration: 9000, action: { label: "Star it", onClick: () => toggleStar({ id: sug.id, title: sug.title }) },
  });
  const canManage = (e) => isAdmin || e.private;
  const openSubmit = () => { if (!user) { toast("Log in to submit an event"); openAuth(); return; } setSubmitOpen(true); };

  const EventRow = ({ e }) => {
    const cm = catMeta(e.category);
    const unpub = isAdmin && isUnpublished(e);
    return (
      <div className={`flex items-start gap-3 rounded-xl border p-3 sm:p-4 transition-colors ${unpub ? "border-dashed border-amber-500/30 bg-[#0e1119] opacity-70" : "border-[#222F43] bg-[#111723] hover:border-[#334566]"} ${isCancelled(e) ? "opacity-60" : ""}`} data-testid={`agenda-${e.id}`}>
        <div className="h-10 w-1 rounded-full shrink-0" style={{ background: cm.color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setDetail(e)} className={`font-bold text-left hover:text-emerald-300 text-[15px] sm:text-base leading-snug ${isCancelled(e) ? "line-through text-slate-400" : ""}`} data-testid={`agenda-title-${e.id}`}>{e.title}</button>
            <EventBadges e={e} />
            {unpub && <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid={`agenda-unpub-${e.id}`}>{e.review === "hidden" ? "hidden" : "not published"}</span>}
          </div>
          <div className="text-xs text-slate-500 font-mono mt-0.5 flex items-center gap-2 flex-wrap">
            {isTbd(e) ? <span className="text-purple-300 flex items-center gap-1"><HelpCircle className="h-3 w-3" /> {e.dateHint || "date not announced"}</span> : fmtDay(e.date)}
            {e.time && !isTbd(e) && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {e.time}</span>}
            {e.status === "rescheduled" && e.dateHistory?.length > 0 && <span className="text-amber-400/80">was {new Date(e.dateHistory[e.dateHistory.length - 1].date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
          </div>
          {e.description && <p className={`text-sm text-slate-300 mt-1.5 ${isMobile ? "line-clamp-2" : ""}`}>{e.description}</p>}
          <div className="flex items-center gap-1.5 flex-wrap mt-2">
            {(e.coins || []).slice(0, isMobile ? 4 : 8).map((c) => <span key={c} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#172030] text-emerald-400 max-w-[120px] truncate">${c}</span>)}
            {e.sourceUrl && <a href={e.sourceUrl} target="_blank" rel="noreferrer" className="text-[11px] text-cyan-400 hover:underline flex items-center gap-1 ml-1"><ExternalLink className="h-3 w-3" /> {e.sourceName || "source"}{e.sources?.length > 1 ? ` +${e.sources.length - 1}` : ""}</a>}
            {e.updated_at && <span className="hidden sm:inline text-[10px] text-slate-600 ml-auto">updated {fmtAgo(e.updated_at)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!e.private && <StarButton starred={isStarred(e.id)} onClick={() => toggleStar(e)} testId={`agenda-star-${e.id}`} />}
          {isAdmin && !e.private && !isMobile && (
            <button onClick={() => togglePublish(e)} title={unpub ? "Publish to Pro members" : "Hide from Pro members"}
              className={`p-2 ${unpub ? "text-amber-400 hover:text-emerald-400" : "text-emerald-400 hover:text-amber-400"}`} data-testid={`agenda-publish-${e.id}`}>
              {unpub ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
          {canManage(e) && !isMobile && (
            <>
              <button onClick={() => openEdit(e)} className="p-2 text-slate-400 hover:text-emerald-400" data-testid={`agenda-edit-${e.id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(e)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`agenda-del-${e.id}`}><Trash2 className="h-4 w-4" /></button>
            </>
          )}
        </div>
      </div>
    );
  };

  const TbdList = () => tbd.length === 0 ? null : (
    <div className="mt-8" data-testid="tbd-list">
      <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-purple-300 mb-3 flex items-center gap-2"><HelpCircle className="h-4 w-4" /> Date TBD <span className="text-slate-500">({tbd.length})</span> <span className="text-slate-500 normal-case tracking-normal font-sans">— announced, no exact day yet</span></h2>
      <div className="space-y-2">{tbd.map((e) => <EventRow key={e.id} e={e} />)}</div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-5 sm:py-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2"><CalendarDays className="h-6 w-6 sm:h-7 sm:w-7 text-emerald-400" /> Catalyst Calendar</h1>
          <p className="text-slate-400 text-sm mt-1 hidden sm:block">Big events that can move coins — politics, launches, releases, macro and more.</p>
          <p className="text-[11px] font-mono text-slate-500 mt-1 hidden sm:block" data-testid="sync-status">
            <RefreshCw className="inline h-3 w-3 mr-1 -mt-0.5 text-cyan-400" />
            Official feeds (Fed · BLS · unlocks) weekly · news scan every 12h
            {syncInfo?.at && <> · last sync {fmtAgo(syncInfo.at)}</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => openMine("")} variant="outline" size={isMobile ? "sm" : "default"} className="tap border-pink-400/50 bg-transparent text-pink-200 hover:bg-pink-500/10" data-testid="my-event-open-btn">
            <Lock className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">My event</span>
          </Button>
          <Button onClick={openSubmit} variant="outline" size={isMobile ? "sm" : "default"} className="tap border-pink-500/50 bg-transparent text-pink-300 hover:bg-pink-500/10" data-testid="submit-event-open-btn">
            <Megaphone className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Submit event</span>
          </Button>
          {isAdmin && !isMobile && (
            <>
              <Button onClick={() => setReviewOpen(true)} variant="outline" className="border-amber-500/50 bg-transparent text-amber-300 hover:bg-amber-500/10" data-testid="review-open-btn">
                <Inbox className="h-4 w-4 mr-1.5" /> Review {pendingCount > 0 && <span className="ml-1.5 text-[10px] font-mono rounded-full bg-amber-500/20 px-1.5" data-testid="review-count">{pendingCount}</span>}
              </Button>
              <Button onClick={() => setSourcesOpen(true)} variant="outline" className="border-cyan-500/50 bg-transparent text-cyan-300 hover:bg-cyan-500/10" data-testid="sources-open-btn">
                <Settings2 className="h-4 w-4 mr-1.5" /> Sources
              </Button>
              <Button onClick={() => openAdd("")} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="catalyst-add-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Add
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 sm:mb-4">
        {view === "calendar" ? (
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" className="border-[#334566] bg-transparent h-9 w-9" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="font-display font-bold text-lg min-w-[140px] sm:min-w-[180px] text-center" data-testid="cal-month-label">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
            <Button size="icon" variant="outline" className="border-[#334566] bg-transparent h-9 w-9" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" className="text-slate-400 hover:text-emerald-400 h-9 hidden sm:inline-flex" onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }} data-testid="cal-today">Today</Button>
          </div>
        ) : <div className="hidden md:block" />}
        <div className="flex items-center gap-1 bg-[#111723] border border-[#222F43] rounded-lg p-1 w-full md:w-auto">
          {isMobile && (
            <button onClick={() => setView("upnext")} className={`tap flex-1 md:flex-none flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${view === "upnext" ? "bg-emerald-500/15 text-emerald-400" : "text-slate-400"}`} data-testid="view-upnext"><Flame className="h-4 w-4" /> Up Next</button>
          )}
          <button onClick={() => setView("calendar")} className={`tap flex-1 md:flex-none flex items-center justify-center gap-1.5 px-3 py-2 md:py-1.5 rounded-md text-sm font-medium transition-colors ${view === "calendar" ? "bg-emerald-500/15 text-emerald-400" : "text-slate-400"}`} data-testid="view-calendar"><LayoutGrid className="h-4 w-4" /> {isMobile ? "Month" : "Calendar"}</button>
          {!isMobile && (
            <button onClick={() => setView("agenda")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "agenda" ? "bg-emerald-500/15 text-emerald-400" : "text-slate-400"}`} data-testid="view-agenda"><List className="h-4 w-4" /> Agenda</button>
          )}
          <button onClick={() => setView("feed")} className={`tap relative flex-1 md:flex-none flex items-center justify-center gap-1.5 px-3 py-2 md:py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${view === "feed" ? "bg-amber-500/15 text-amber-400" : "text-slate-400"}`} data-testid="view-feed">
            <Star className="h-4 w-4" fill={view === "feed" ? "currentColor" : "none"} /> {isMobile ? "Feed" : "My Feed"}
            {starredIds.size > 0 && <span className="text-[10px] font-mono rounded-full bg-amber-500/20 text-amber-300 px-1.5" data-testid="feed-count">{starredIds.size}</span>}
            {alerts.length > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="feed-alert-badge">{alerts.length}</span>}
          </button>
        </div>
      </div>

      <div className="flex flex-nowrap sm:flex-wrap overflow-x-auto no-scrollbar gap-x-3 sm:gap-x-4 gap-y-1.5 mb-3 sm:mb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="catalyst-legend">
        {CATEGORY_ORDER.map((c) => (
          <span key={c} className="shrink-0 flex items-center gap-1.5 text-xs text-slate-400"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CATEGORY_META[c].color }} /> {CATEGORY_META[c].label}</span>
        ))}
      </div>

      {isAdmin && !isMobile && (
        <div className="flex flex-wrap items-center gap-2 mb-4 rounded-xl border border-amber-500/20 bg-amber-500/5 px-3 py-2" data-testid="curation-bar">
          <span className="text-[11px] font-mono uppercase tracking-widest text-amber-300/90 flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> Curation</span>
          <span className="text-xs text-slate-400">Pro members only see published events. Dimmed = not published yet.</span>
          <div className="ml-auto flex items-center gap-1 bg-[#111723] border border-[#222F43] rounded-lg p-0.5">
            {[["all", `All ${publishedCount + unpublishedCount}`], ["published", `Published ${publishedCount}`], ["unpublished", `Unpublished ${unpublishedCount}`]].map(([k, label]) => (
              <button key={k} onClick={() => setPubFilter(k)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${pubFilter === k ? (k === "unpublished" ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-400") : "text-slate-400 hover:text-slate-200"}`} data-testid={`pub-filter-${k}`}>{label}</button>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>}

      <div className="relative">
      {!loading && locked && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-8 sm:pt-14 px-2" data-testid="calendar-locked">
          <div className="w-full max-w-2xl">
            <UpgradeCard testId="calendar-paywall-card" title="See every catalyst before it moves the market"
              subtitle={`${events.length} events on this calendar — FOMC, CPI, token unlocks, Elon reveals, ETF deadlines. Pro members see them all, star the ones for coins they hold, and get alerted when dates change.`} compact />
          </div>
        </div>
      )}

      {!loading && view === "upnext" && (
        <UpNextList upcoming={upcoming} todayKey={todayKey} locked={locked} renderRow={(e) => <EventRow key={e.id} e={e} />} tbd={<TbdList />} />
      )}

      {!loading && view === "calendar" && (
        <div className={locked ? "blur-[4px] select-none pointer-events-none" : ""} aria-hidden={locked}>
          <div className="rounded-xl border border-[#222F43] overflow-hidden" data-testid="calendar-grid">
            <div className="grid grid-cols-7 bg-[#111723] border-b border-[#222F43]">
              {WEEKDAYS.map((w) => <div key={w} className="px-1 sm:px-2 py-2 text-[10px] sm:text-[11px] font-mono uppercase tracking-wider text-slate-500 text-center">{w.slice(0, isMobile ? 1 : 3)}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {grid.map((d, i) => {
                const k = dayKey(d); const inMonth = d.getMonth() === cursor.getMonth();
                const dayEvents = byDay[k] || []; const isToday = k === todayKey;
                if (isMobile) {
                  const colors = [...new Set(dayEvents.map((e) => catMeta(e.category).color))].slice(0, 4);
                  return (
                    <button key={i} onClick={() => dayEvents.length && setDayOpen(k)} disabled={!dayEvents.length} data-testid={`day-${k}`}
                      className={`tap aspect-square border-b border-r border-[#222F43] flex flex-col items-center justify-center gap-1 ${inMonth ? "bg-[#0c111b]" : "bg-[#0a0e16]"} ${dayEvents.length ? "" : "opacity-60"}`}>
                      <span className={`text-xs font-mono ${isToday ? "h-6 w-6 rounded-full bg-emerald-500 text-black font-bold flex items-center justify-center" : inMonth ? "text-slate-300" : "text-slate-700"}`}>{d.getDate()}</span>
                      <span className="flex gap-0.5 h-1.5">{colors.map((c) => <span key={c} className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />)}</span>
                      {dayEvents.length > 0 && <span className="text-[9px] font-mono text-slate-500 leading-none">{dayEvents.length}</span>}
                    </button>
                  );
                }
                return (
                  <div key={i} onClick={() => (isAdmin ? openAdd(k) : pro ? openMine(k) : null)}
                    className={`min-h-[96px] border-b border-r border-[#222F43] p-1.5 ${inMonth ? "bg-[#0c111b]" : "bg-[#0a0e16]"} ${isAdmin || pro ? "cursor-pointer hover:bg-[#121a28]" : ""} transition-colors`}
                    data-testid={`day-${k}`}>
                    <div className={`text-xs font-mono mb-1 ${isToday ? "h-5 w-5 rounded-full bg-emerald-500 text-black font-bold flex items-center justify-center" : inMonth ? "text-slate-400" : "text-slate-700"}`}>{d.getDate()}</div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((e) => {
                        const cm = catMeta(e.category);
                        const unpub = isAdmin && isUnpublished(e);
                        return (
                          <div key={e.id} className={`flex items-stretch rounded overflow-hidden ${unpub ? "opacity-45 saturate-50" : ""}`}
                            style={{ background: cm.color + "22", borderLeft: `2px solid ${cm.color}`, outline: unpub ? "1px dashed rgba(251,191,36,.45)" : "none" }}>
                          <button onClick={(ev) => { ev.stopPropagation(); setDetail(e); }}
                            className={`flex-1 min-w-0 text-left truncate px-1.5 py-0.5 text-[10px] font-medium leading-tight hover:brightness-125 transition-all ${isCancelled(e) ? "line-through opacity-50" : ""}`}
                            style={{ color: cm.color }}
                            data-testid={`event-pill-${e.id}`} title={e.title}>
                            {e.viral && <Flame className="inline h-2.5 w-2.5 mr-0.5 -mt-0.5 text-orange-400" data-testid={`viral-flame-${e.id}`} />}{e.private ? <Lock className="inline h-2.5 w-2.5 mr-0.5 -mt-0.5 text-pink-300" /> : isStarred(e.id) && <Star className="inline h-2.5 w-2.5 mr-0.5 -mt-0.5 text-amber-400" fill="currentColor" />}
                            {e.status === "rescheduled" && <span className="text-amber-400 mr-0.5">↻</span>}{e.title}
                          </button>
                          {isAdmin && !e.private && (
                            <button onClick={(ev) => { ev.stopPropagation(); togglePublish(e); }} title={unpub ? "Publish" : "Hide"}
                              className={`hidden sm:block px-1 shrink-0 ${unpub ? "text-amber-300 hover:text-emerald-300" : "text-emerald-400/70 hover:text-amber-300"}`} data-testid={`pill-publish-${e.id}`}>
                              {unpub ? <EyeOff className="h-2.5 w-2.5" /> : <Eye className="h-2.5 w-2.5" />}
                            </button>
                          )}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && <div className="text-[10px] text-slate-500 pl-1">+{dayEvents.length - 3} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <TbdList />
        </div>
      )}

      {!loading && (view === "agenda" || view === "feed") && (
        <div data-testid={view === "feed" ? "feed-list" : "agenda-list"} className={locked ? "blur-[4px] select-none pointer-events-none" : ""} aria-hidden={locked}>
          {view === "feed" && user && alerts.length > 0 && (
            <div className="mb-4 rounded-xl border border-red-500/40 bg-red-500/5 p-3 space-y-1.5" data-testid="feed-alerts">
              <div className="text-xs font-mono uppercase tracking-widest text-red-300 flex items-center gap-1.5"><Bell className="h-3.5 w-3.5" /> Changes to your starred events</div>
              {alerts.map((a) => <div key={a.id} className="text-sm text-slate-200" data-testid={`feed-alert-${a.id}`}>{a.message} <span className="text-slate-500 text-xs">· {fmtAgo(a.at)}</span></div>)}
            </div>
          )}
          {view === "feed" && !user && (
            <div className="text-center py-16 text-slate-500" data-testid="feed-login-prompt">
              <Star className="h-8 w-8 mx-auto mb-3 text-amber-400/60" />
              <p className="mb-3">Log in to star catalysts and build your personal coming-up feed.</p>
              <Button onClick={openAuth} className="bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="feed-login-btn">Log in</Button>
            </div>
          )}
          {view === "feed" && user && listed.length === 0 && (
            <div className="text-center py-16 text-slate-500" data-testid="feed-empty">
              <Star className="h-8 w-8 mx-auto mb-3 text-amber-400/60" />
              Nothing here yet. Hit the <Star className="inline h-3.5 w-3.5 text-amber-400 -mt-0.5" /> on any upcoming event, or add your own with <Lock className="inline h-3.5 w-3.5 text-pink-300 -mt-0.5" /> My event.
            </div>
          )}
          {view === "agenda" && listed.length === 0 && <div className="text-center py-16 text-slate-500">No upcoming catalysts yet.</div>}
          <div className="space-y-2">{(view === "agenda" || user) && listed.map((e) => <EventRow key={e.id} e={e} />)}</div>
          {view === "agenda" && <TbdList />}
        </div>
      )}
      </div>

      <CatalystDetail event={detail} onClose={() => setDetail(null)} isAdmin={isAdmin && !isMobile} isStarred={detail ? isStarred(detail.id) : false}
        onStar={toggleStar} onEdit={openEdit} onDelete={del} onTogglePublish={togglePublish} onUnlock={unlockEvent} />
      <Dialog open={Boolean(dayOpen)} onOpenChange={(o) => !o && setDayOpen(null)}>
        <DialogContent className="glass border-[#334566] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="day-sheet">
          <DialogHeader>
            <DialogTitle className="font-display">{dayOpen && new Date(dayOpen + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">{(byDay[dayOpen] || []).map((e) => <EventRow key={e.id} e={e} />)}</div>
        </DialogContent>
      </Dialog>
      <CatalystForm open={formOpen} onOpenChange={setFormOpen} event={editEvent} defaultDate={defaultDate} onSaved={refreshAll} privateMode={formPrivate} onSuggestion={onSuggestion} />
      <SubmitEventDialog open={submitOpen} onOpenChange={setSubmitOpen} />
      {isAdmin && <ReviewQueue open={reviewOpen} onOpenChange={setReviewOpen} onChanged={refreshAll} onEdit={openEdit} />}
      {isAdmin && <SourcesPanel open={sourcesOpen} onOpenChange={setSourcesOpen} onChanged={refreshAll} />}
    </div>
  );
}
